/**
 * DomainLibrary component for the admin UI.
 * Displays pre-built public UMLS medical glossaries with expand/collapse per specialty.
 * SuperAdmin/PlatformAdmin users can trigger a harvest pipeline refresh.
 * Admin users can import any specialty into a selected tenant's glossaries.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, Download, AlertCircle, Loader2, CheckCircle, Search } from 'lucide-react';
import glossaryService from '../services/glossaryService';
import { trackEvent, trackException } from '../services/appInsightsService';
import HarvestPipelineStatus from './HarvestPipelineStatus';
import './DomainLibrary.css';

const HARVEST_POLL_INTERVAL_MS = 60000;       // 60 seconds (was 5000)
const HARVEST_FAST_POLL_INTERVAL_MS = 30000;  // 30 seconds for manual trigger fast-poll phase
const HARVEST_FAST_POLL_COUNT = 5;            // fast-poll for first 5 polls after manual trigger
const MAX_POLL_FAILURES = 5;

// Pipeline states where re-triggering Step 1 would overwrite in-progress work.
// Used both for the Trigger Harvest button guard and the blob/runtime reconciliation check.
const ACTIVE_PIPELINE_STATUSES = [
    'running', 'paused', 'step1_complete', 'batch_submitted',
    'step2_batch_submitted', 'step2_batch_submitting', 'step2_preparing',
    'batch_polling', 'step2_distributing'
];

/**
 * Maps triggeredByType API value to a human-readable label.
 * @param {string} type
 * @returns {string}
 */
const formatTriggerType = (type) => {
    if (type === 'http') return 'Manual';
    if (type === 'timer') return 'Scheduled';
    return type || 'Unknown';
};

/**
 * Returns a relative time string for a past ISO date (e.g. "12 minutes ago").
 * @param {string} isoDate
 * @returns {string}
 */
const getRelativeTime = (isoDate) => {
    if (!isoDate) return '';
    const diffMs = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
};

/**
 * Returns a human-readable duration string between two ISO date strings.
 * @param {string} startedAt
 * @param {string} completedAt
 * @returns {string}
 */
const getDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '';
    const diffMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return 'less than a minute';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return remaining > 0 ? `${hours}h ${remaining}min` : `${hours}h`;
};

/**
 * Formats an ISO date string into a human-readable date/time string.
 * @param {string} isoDate
 * @returns {string}
 */
const formatDateTime = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

/**
 * LLM percentage threshold above which a language is flagged as high-risk.
 * Mirrors the Harvest.HighLlmWarning threshold in HarvestMedicalGlossaryFunction.cs.
 */
const LLM_THRESHOLD = 40.0;

/** CSS class suffix per canonical source bucket. */
const SOURCE_CHIP_CLASS = {
    snomed: 'sc-snomed',
    icd11: 'sc-icd11',
    loinc: 'sc-loinc',
    'nci-thesaurus': 'sc-nci',
    mesh: 'sc-mesh',
    wikidata: 'sc-wikidata',
    'safety-blocked': 'sc-safety',
    llm: 'sc-llm',
    meddra: 'sc-meddra',
};

/**
 * Renders compact colored chips for each entry in a sourceCounts map.
 * Chips are sorted descending by count so the dominant source appears first.
 *
 * @param {{ sourceCounts: Object.<string,number>|undefined }} props
 */
const SourceCountChips = ({ sourceCounts }) => {
    if (!sourceCounts || Object.keys(sourceCounts).length === 0) return null;
    return (
        <span className="dl-source-counts" data-testid="source-counts">
            {Object.entries(sourceCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([src, cnt]) => (
                    <span
                        key={src}
                        className={`dl-sc-chip ${SOURCE_CHIP_CLASS[src] || 'sc-other'}`}
                        title={`${cnt} terms from ${src}`}
                    >
                        {src}: {cnt}
                    </span>
                ))}
        </span>
    );
};

/**
 * Renders a per-specialty, per-language coverage summary table.
 * Shown inside the success banner when harvest status is "completed"
 * and specialtyCoverage contains entries. Visible to SuperAdmins only.
 *
 * @param {{ specialtyCoverage: Array }} props
 * @returns {JSX.Element|null}
 */
const CoverageSummary = ({ specialtyCoverage }) => {
    const [expandedSpecialty, setExpandedSpecialty] = useState(null);

    if (!specialtyCoverage || specialtyCoverage.length === 0) return null;

    const handleToggle = (specialty) => {
        setExpandedSpecialty(prev => (prev === specialty ? null : specialty));
    };

    return (
        <div className="dl-coverage-summary" data-testid="coverage-summary">
            <h3 className="dl-coverage-title">Coverage Report</h3>
            {specialtyCoverage.map((entry) => {
                const isExpanded = expandedSpecialty === entry.specialty;
                const noCoverageCount = entry.languages.filter(l => l.termCount === 0).length;
                const highLlmCount = entry.languages.filter(l => l.llmPct > LLM_THRESHOLD && l.termCount > 0).length;

                return (
                    <div
                        key={entry.specialty}
                        className="dl-coverage-specialty"
                    >
                        <div
                            className="dl-coverage-specialty-header"
                            data-testid={`coverage-${entry.specialty}`}
                            onClick={() => handleToggle(entry.specialty)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleToggle(entry.specialty);
                                }
                            }}
                            aria-expanded={isExpanded}
                        >
                            <span className="dl-coverage-expand-icon">
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            <span className="dl-coverage-specialty-name">{entry.specialty}</span>
                            <span className="dl-badge">{entry.totalTerms} terms</span>
                            <span className="dl-badge">{entry.languages.length} languages</span>
                            {noCoverageCount > 0 && (
                                <span
                                    className="dl-coverage-flag dl-flag-red"
                                    data-testid={`no-coverage-flag-${entry.specialty}`}
                                >
                                    {noCoverageCount} no coverage
                                </span>
                            )}
                            {highLlmCount > 0 && (
                                <span
                                    className="dl-coverage-flag dl-flag-amber"
                                    data-testid={`high-llm-flag-${entry.specialty}`}
                                >
                                    {highLlmCount} high LLM%
                                </span>
                            )}
                        </div>
                        {isExpanded && (
                            <div
                                className="dl-coverage-language-table"
                                data-testid={`coverage-languages-${entry.specialty}`}
                            >
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Language</th>
                                            <th>Terms</th>
                                            <th>LLM %</th>
                                            <th>Sources</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entry.languages.map((lang) => (
                                            <tr
                                                key={lang.code}
                                                data-testid={`coverage-lang-${lang.code}`}
                                            >
                                                <td>{lang.code.toUpperCase()}</td>
                                                <td>{lang.termCount}</td>
                                                <td>{lang.llmPct.toFixed(1)}%</td>
                                                <td>
                                                    <SourceCountChips sourceCounts={lang.sourceCounts} />
                                                </td>
                                                <td>
                                                    {lang.termCount === 0 && (
                                                        <span
                                                            className="dl-coverage-badge dl-badge-red"
                                                            data-testid={`badge-no-coverage-${lang.code}`}
                                                        >
                                                            No Coverage
                                                        </span>
                                                    )}
                                                    {lang.termCount > 0 && lang.llmPct > LLM_THRESHOLD && (
                                                        <span
                                                            className="dl-coverage-badge dl-badge-amber"
                                                            data-testid={`badge-high-llm-${lang.code}`}
                                                        >
                                                            High LLM%
                                                        </span>
                                                    )}
                                                    {lang.termCount > 0 && lang.llmPct <= LLM_THRESHOLD && (
                                                        <span className="dl-coverage-badge dl-badge-green">
                                                            Good
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Returns a quality label based on verified percentage.
 * @param {{ verifiedPct: number }} lang
 * @returns {'Verified' | 'Partial' | 'Pending Review'}
 */
const getQualityLabel = (lang) => {
    if (lang.verifiedPct >= 100) return 'Verified';
    if (lang.verifiedPct >= 50) return 'Partial';
    return 'Pending Review';
};

/**
 * Language coverage for every medical specialty, stated instead of a language count (#1596, owner
 * decision B 2026-10-10). The old badge counted every language with any translation ("53 languages"),
 * but Spanish (native SNOMED SCTSPA) is the only complete language; ~20 others sit at 14-18% of
 * concepts and the rest have none (tasks/issues/2026-07-07-medical-glossary-language-coverage-gap.md).
 * ponytail: a fixed statement, not computed. The library's per-language stats include the reciprocal
 * es->en/ko... entries, so they cannot measure per-concept coverage; compute it from a server field
 * once one exists.
 */
const LANGUAGE_COVERAGE_LABEL = 'Spanish full · other languages partial (~15%)';

/**
 * Single specialty row with expand/collapse and action buttons.
 */
const SpecialtyItem = ({ specialty, tenantId, isSuperAdmin, onImported, onError }) => {
    const [expanded, setExpanded] = useState(false);
    const [importing, setImporting] = useState(false);
    const [previewView, setPreviewView] = useState('coverage');
    const [terms, setTerms] = useState(null);
    const [termsLoading, setTermsLoading] = useState(false);
    const [termsError, setTermsError] = useState(null);
    const [displayCount, setDisplayCount] = useState(30);
    const termsLoadedRef = useRef(false);
    const isDisabled = specialty.termCount === 0;

    const loadTerms = useCallback(async () => {
        if (termsLoadedRef.current) return;
        termsLoadedRef.current = true;
        setTermsLoading(true);
        setTermsError(null);
        try {
            const data = await glossaryService.getPublicSpecialty(specialty.medicalSpecialty);
            setTerms(data.entries || []);
        } catch (err) {
            termsLoadedRef.current = false;
            trackException(err, { feature: 'DomainLibrary', operation: 'getPublicSpecialty', medicalSpecialty: specialty.medicalSpecialty });
            setTermsError('Term preview unavailable — import to access full terms');
        } finally {
            setTermsLoading(false);
        }
    }, [specialty.medicalSpecialty]);

    const handleImport = async (e) => {
        e.stopPropagation();
        if (!tenantId) {
            onError('Select a tenant first before importing.');
            return;
        }
        setImporting(true);
        try {
            await glossaryService.importPublicGlossary(specialty.medicalSpecialty, tenantId);
            trackEvent('DomainLibrary.GlossaryImported', { medicalSpecialty: specialty.medicalSpecialty, tenantId });
            if (onImported) onImported(specialty.medicalSpecialty);
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'importPublicGlossary' });
            if (onError) onError(err.message || 'Failed to import glossary');
        } finally {
            setImporting(false);
        }
    };

    return (
        <div
            className={`dl-specialty-item${isDisabled ? ' dl-disabled' : ''}`}
            data-testid={`specialty-${specialty.medicalSpecialty}`}
        >
            <div
                className="dl-specialty-header"
                onClick={() => !isDisabled && setExpanded(prev => !prev)}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) { e.preventDefault(); setExpanded(prev => !prev); } }}
                aria-expanded={expanded}
            >
                <span className="dl-expand-icon">
                    {isDisabled ? null : expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
                <span className="dl-specialty-name">{specialty.medicalSpecialty}</span>
                <div className="dl-specialty-meta">
                    <span className="dl-badge">{specialty.termCount} terms</span>
                    <span className="dl-badge">{LANGUAGE_COVERAGE_LABEL}</span>
                    {specialty.umlsOnly && (
                        <span className="dl-source-badge dl-umls" title="Verified UMLS source">UMLS</span>
                    )}
                    {!specialty.umlsOnly && !isDisabled && (
                        <span className="dl-source-badge dl-verified" title="Verified source">Verified</span>
                    )}
                </div>
                {!isDisabled && (
                    <button
                        type="button"
                        className="dl-import-btn"
                        onClick={handleImport}
                        disabled={importing || !tenantId}
                        title={!tenantId ? 'Select a tenant above to import' : `Import ${specialty.medicalSpecialty} into tenant glossaries`}
                    >
                        {importing ? <Loader2 size={14} className="dl-spin" /> : <Download size={14} />}
                        {importing ? 'Importing…' : 'Import to Tenant'}
                    </button>
                )}
            </div>

            {expanded && !isDisabled && (
                <div className="dl-specialty-preview" data-testid={`specialty-preview-${specialty.medicalSpecialty}`}>
                    <div className="dl-preview-tabs">
                        <button
                            type="button"
                            className={`dl-preview-tab${previewView === 'coverage' ? ' dl-tab-active' : ''}`}
                            onClick={() => setPreviewView('coverage')}
                        >
                            Coverage
                        </button>
                        <button
                            type="button"
                            className={`dl-preview-tab${previewView === 'terms' ? ' dl-tab-active' : ''}`}
                            onClick={() => { setPreviewView('terms'); loadTerms(); }}
                        >
                            Terms
                        </button>
                    </div>

                    {previewView === 'coverage' && (
                        specialty.languages && specialty.languages.length > 0 ? (
                            <div className="dl-language-list">
                                {specialty.languages.map((lang) => {
                                    const quality = getQualityLabel(lang);
                                    const isLow = lang.verifiedPct < 50;
                                    return (
                                        <div key={lang.code} className="dl-language-row" data-testid={`language-${lang.code}`}>
                                            <span className="dl-lang-code">{lang.code.toUpperCase()}</span>
                                            <span className={`dl-quality-badge dl-q-${quality.toLowerCase().replace(' ', '-')}`}>
                                                {quality}
                                            </span>
                                            {isLow && (
                                                <span className="dl-warning" title="Low coverage — LLM-assisted translations">!</span>
                                            )}
                                            {lang.pendingCount > 0 && (
                                                <span className="dl-pending">{lang.pendingCount} pending</span>
                                            )}
                                            <SourceCountChips sourceCounts={lang.sourceCounts} />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="dl-no-data">
                                {specialty.lastUpdated ? `Last updated: ${new Date(specialty.lastUpdated).toLocaleDateString()}` : 'No language detail available.'}
                            </p>
                        )
                    )}

                    {previewView === 'terms' && (
                        termsLoading ? (
                            <div className="dl-terms-loading">
                                <Loader2 size={14} className="dl-spin" /> Loading terms…
                            </div>
                        ) : termsError ? (
                            <p className="dl-no-data">{termsError}</p>
                        ) : terms && terms.length > 0 ? (
                            <div className="dl-terms-preview">
                                <table className="dl-terms-table">
                                    <thead>
                                        <tr>
                                            <th>Source</th>
                                            <th>Language</th>
                                            <th>Translation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {terms.slice(0, displayCount).map((entry, idx) => (
                                            <tr key={`${entry.sourceText}-${entry.targetLanguage}-${idx}`}>
                                                <td className="dl-term-source">{entry.sourceText}</td>
                                                <td className="dl-term-lang">{entry.targetLanguage}</td>
                                                <td className="dl-term-target">{entry.targetText}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {terms.length > displayCount ? (
                                    <div className="dl-terms-more">
                                        <span>Showing {displayCount} of {terms.length} entries</span>
                                        <button
                                            className="dl-load-more-btn"
                                            onClick={() => setDisplayCount(prev => prev + 30)}
                                        >
                                            Load More
                                        </button>
                                    </div>
                                ) : terms.length > 30 ? (
                                    <p className="dl-terms-more">Showing all {terms.length} entries</p>
                                ) : null}
                            </div>
                        ) : terms !== null ? (
                            <p className="dl-no-data">No terms available for this specialty.</p>
                        ) : null
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * DomainLibrary — main component for the admin Glossary Management Domain Library tab.
 * @param {{ tenantId: string, tenants: Array, isSuperAdmin: boolean, isAdmin: boolean, setTenantId: function }} props
 */
const DomainLibrary = ({ tenantId, isSuperAdmin, isAdmin }) => {
    const [specialties, setSpecialties] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [harvesting, setHarvesting] = useState(false);
    const [harvestNotice, setHarvestNotice] = useState(null);
    const [harvestNoticeType, setHarvestNoticeType] = useState('info'); // 'info' | 'success' | 'error'
    const [importedToast, setImportedToast] = useState('');
    const [importingAll, setImportingAll] = useState(false);
    const [importAllProgress, setImportAllProgress] = useState(null); // { current: 2, total: 6 }
    const [lastHarvestStatus, setLastHarvestStatus] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [showStopConfirm, setShowStopConfirm] = useState(false);
    const [approvingStep2, setApprovingStep2] = useState(false);
    const [step2JustApproved, setStep2JustApproved] = useState(false);
    const [retryingStep2, setRetryingStep2] = useState(false);
    const [step2RetrySubmitted, setStep2RetrySubmitted] = useState(false);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [skipUmls, setSkipUmls] = useState(true);
    const [autoApproveStep2, setAutoApproveStep2] = useState(false);
    const [forceNewRun, setForceNewRun] = useState(false);
    const [specialtyFilter, setSpecialtyFilter] = useState('');
    const pollIntervalRef = useRef(null);
    const consecutivePollFailuresRef = useRef(0);
    const fastPollCountRef = useRef(0);
    const lastKnownStateRef = useRef(null);
    // Tracks the current harvest instanceId for passing to getHarvestStatus.
    // Updated whenever lastHarvestStatus changes and umlsVersion is known.
    const pollInstanceIdRef = useRef(null);

    // Keep pollInstanceIdRef in sync with lastHarvestStatus.umlsVersion so polling calls
    // can send instanceId without creating a stale closure in startPolling.
    // This enables the backend to include orchestrationStatus for blob/runtime reconciliation.
    useEffect(() => {
        if (lastHarvestStatus && lastHarvestStatus.umlsVersion) {
            pollInstanceIdRef.current = `harvest-${lastHarvestStatus.umlsVersion}`;
        }
    }, [lastHarvestStatus]);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearTimeout(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const loadLibrary = useCallback(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        glossaryService.getPublicDomainLibrary()
            .then((data) => { if (!cancelled) { setSpecialties(data); setLoading(false); setLastRefreshed(new Date()); } })
            .catch((err) => {
                if (!cancelled) { setError(err.message || 'Failed to load domain library'); setLoading(false); }
                trackException(err, { feature: 'DomainLibrary', operation: 'getPublicDomainLibrary' });
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => loadLibrary(), [loadLibrary]);

    const startPolling = useCallback((fastPoll = false) => {
        stopPolling();
        consecutivePollFailuresRef.current = 0;
        fastPollCountRef.current = 0;

        const getInterval = () => {
            if (fastPoll && fastPollCountRef.current < HARVEST_FAST_POLL_COUNT) {
                return HARVEST_FAST_POLL_INTERVAL_MS;
            }
            return HARVEST_POLL_INTERVAL_MS;
        };

        const schedulePoll = () => {
            pollIntervalRef.current = setTimeout(async () => {
                try {
                    // Derive instanceId from the ref so we can pass it without a stale closure.
                    // This allows the backend to include orchestrationStatus for reconciliation.
                    const pollInstanceId = pollInstanceIdRef.current;
                    const status = await glossaryService.getHarvestStatus(pollInstanceId || undefined);
                    consecutivePollFailuresRef.current = 0;
                    const { status: state, currentSpecialty, specialtiesCompleted, specialtiesTotal } = status;

                    // Reconciliation: if blob says active but runtime is dead, surface error and stop.
                    // Mirrors the mount-time check — detects orchestrator crash during polling.
                    const deadRuntimeStates = ['Terminated', 'Completed', 'Failed'];
                    if (status.orchestrationStatus &&
                        deadRuntimeStates.includes(status.orchestrationStatus) &&
                        ACTIVE_PIPELINE_STATUSES.includes(state)) {
                        stopPolling();
                        setHarvesting(false);
                        setHarvestNotice(`Warning: Pipeline status shows "${state}" but the orchestration is ${status.orchestrationStatus}. The pipeline may need to be re-triggered.`);
                        setHarvestNoticeType('error');
                        setLastHarvestStatus({ ...status, status: status.orchestrationStatus.toLowerCase() });
                        trackEvent('DomainLibrary.HarvestPollingReconciliation', {
                            blobStatus: state,
                            orchestrationStatus: status.orchestrationStatus,
                        });
                        return;
                    }

                    if (state === 'running') {
                        setLastHarvestStatus(status);
                        setHarvestNotice(`Harvesting ${currentSpecialty || '…'} (${specialtiesCompleted || 0}/${specialtiesTotal || 8})`);
                        setHarvestNoticeType('info');
                        if (fastPoll) fastPollCountRef.current += 1;
                        schedulePoll();
                    } else if (state === 'paused') {
                        setLastHarvestStatus(status);
                        setHarvestNotice(null);
                        setHarvestNoticeType('info');
                        schedulePoll(); // keep polling so we detect resume
                    } else if (state === 'step1_complete') {
                        setLastHarvestStatus(status);
                        setHarvesting(false);
                        setHarvestNotice(null);
                        setHarvestNoticeType('info');
                        trackEvent('DomainLibrary.HarvestStep1Complete');
                        // Do not stop polling — user may approve Step 2, which transitions to running again
                        schedulePoll();
                    } else if (state === 'batch_polling' || state === 'batch_submitted' || state === 'step2_distributing' || state === 'step2_batch_submitting' || state === 'step2_preparing') {
                        setLastHarvestStatus(status);
                        setHarvestNotice(null);
                        setHarvestNoticeType('info');
                        setStep2JustApproved(false); // clear local approved flag — status has moved past step1_complete
                        schedulePoll();
                    } else if (state === 'stopped' || state === 'completed_step1_only') {
                        stopPolling();
                        setHarvesting(false);
                        setLastHarvestStatus(status);
                        setHarvestNotice(null);
                        trackEvent('DomainLibrary.HarvestStopped', { state });
                    } else if (state === 'completed') {
                        stopPolling();
                        setHarvesting(false);
                        setLastHarvestStatus(status);
                        setHarvestNotice('Harvest completed successfully. Library refreshed.');
                        setHarvestNoticeType('success');
                        trackEvent('DomainLibrary.HarvestPollingCompleted');
                        loadLibrary();
                    } else if (state === 'failed') {
                        stopPolling();
                        setHarvesting(false);
                        setLastHarvestStatus(status);
                        setHarvestNotice('Harvest failed. Check logs for details.');
                        setHarvestNoticeType('error');
                        trackEvent('DomainLibrary.HarvestPollingFailed');
                    }
                } catch (pollErr) {
                    consecutivePollFailuresRef.current += 1;
                    // Track only the first failure to avoid flooding App Insights on sustained outages
                    if (consecutivePollFailuresRef.current === 1) {
                        trackException(pollErr, { feature: 'DomainLibrary', operation: 'harvestStatusPoll' });
                    }
                    // Stop polling after MAX_POLL_FAILURES consecutive failures and surface a notice
                    if (consecutivePollFailuresRef.current >= MAX_POLL_FAILURES) {
                        stopPolling();
                        setHarvesting(false);
                        setHarvestNotice('Unable to reach harvest status endpoint. Please refresh the page.');
                        setHarvestNoticeType('error');
                    } else {
                        schedulePoll();
                    }
                }
            }, getInterval());
        };

        schedulePoll();
    }, [stopPolling, loadLibrary]);

    // On mount, check harvest status and display the appropriate banner for any state
    useEffect(() => {
        if (!isSuperAdmin) return;
        let cancelled = false;

        // Phase 1: fetch status (no instanceId yet — umlsVersion not known).
        // Phase 2: if status is active AND umlsVersion is present, re-fetch with instanceId so
        //          the backend can return orchestrationStatus for blob/runtime reconciliation.
        //          This is a second lightweight GET that only fires when the harvest is active.
        glossaryService.getHarvestStatus()
            .then((firstStatus) => {
                if (cancelled) return;
                const instanceId = firstStatus.umlsVersion ? `harvest-${firstStatus.umlsVersion}` : null;
                if (instanceId && ACTIVE_PIPELINE_STATUSES.includes(firstStatus.status)) {
                    // Re-fetch with instanceId so orchestrationStatus is included in the response
                    return glossaryService.getHarvestStatus(instanceId);
                }
                return firstStatus;
            })
            .then((status) => {
                if (cancelled) return;
                setLastHarvestStatus(status);
                const s = status.status;

                // Reconciliation check: if blob says active but runtime disagrees, show warning.
                // This detects the case where the orchestration was lost (timed out, terminated)
                // but the status blob was never updated (e.g., crash before MarkStopped).
                // orchestrationStatus is present because we passed instanceId in Phase 2 above.
                const deadRuntimeStates = ['Terminated', 'Completed', 'Failed'];
                if (status.orchestrationStatus &&
                    deadRuntimeStates.includes(status.orchestrationStatus) &&
                    ACTIVE_PIPELINE_STATUSES.includes(s)) {
                    setHarvestNotice(`Warning: Pipeline status shows "${s}" but the orchestration is ${status.orchestrationStatus}. The pipeline may need to be re-triggered.`);
                    setHarvestNoticeType('error');
                    setHarvesting(false);
                    // Override local status so isHarvestActive evaluates to false and the
                    // Trigger Harvest button unlocks. The blob still says "running" but the
                    // orchestration is dead — the user needs to be able to re-trigger.
                    setLastHarvestStatus({ ...status, status: status.orchestrationStatus.toLowerCase() });
                    return;
                }
                if (s === 'running') {
                    setHarvesting(true);
                    setHarvestNotice(`Harvesting ${status.currentSpecialty || '…'} (${status.specialtiesCompleted || 0}/${status.specialtiesTotal || 8})`);
                    setHarvestNoticeType('info');
                    startPolling(false); // mount resume — always use normal 60s interval
                } else if (s === 'paused') {
                    setHarvestNotice(null);
                    startPolling(false); // still active — keep polling for resume/stop
                } else if (s === 'step1_complete' || s === 'batch_polling' || s === 'batch_submitted' || s === 'step2_batch_submitted' || s === 'step2_batch_submitting' || s === 'step2_preparing' || s === 'step2_distributing') {
                    setHarvestNotice(null);
                    startPolling(false); // active state — keep polling
                } else if (s === 'completed') {
                    setHarvestNotice('Harvest completed successfully. Library refreshed.');
                    setHarvestNoticeType('success');
                } else if (s === 'failed') {
                    setHarvestNotice('Harvest failed. Check logs for details.');
                    setHarvestNoticeType('error');
                }
                // 'idle', 'stopped', 'completed_step1_only' — no polling needed
            })
            .catch((mountErr) => {
                if (!cancelled) {
                    trackException(mountErr, { feature: 'DomainLibrary', operation: 'mountHarvestStatusCheck' });
                }
            });
        return () => { cancelled = true; };
        // Intentionally excludes startPolling — it is a stable useCallback and this is a mount-only
        // check that must re-run only when isSuperAdmin changes (login state), not on every poll.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin]);

    // Background auto-refresh: poll harvest-status every 60 s for non-superadmin admins.
    // SuperAdmins already get an automatic reload via startPolling (running → completed path),
    // so this effect is intentionally skipped for them to avoid a double-load race.
    // Regular viewers (neither isAdmin nor isSuperAdmin) have no harvest visibility and are skipped
    // to avoid generating unnecessary 403 errors on the harvest-status endpoint.
    useEffect(() => {
        if (isSuperAdmin || !isAdmin) return;
        const interval = setInterval(async () => {
            try {
                const status = await glossaryService.getHarvestStatus();
                const prev = lastKnownStateRef.current;
                lastKnownStateRef.current = status.status;
                if (prev === 'running' && status.status === 'completed') {
                    loadLibrary();
                }
            } catch {
                // Non-fatal — suppress polling errors silently
            }
        }, HARVEST_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [loadLibrary, isSuperAdmin, isAdmin]);

    const handleTriggerHarvest = async () => {
        setHarvesting(true);
        setHarvestNotice(null);
        setStep2JustApproved(false);
        setStep2RetrySubmitted(false);
        const options = {};
        if (skipUmls) options.skipUmls = true;
        if (autoApproveStep2) options.autoApproveStep2 = true;
        if (forceNewRun) options.forceNewRun = true;
        trackEvent('DomainLibrary.HarvestTriggered', { triggeredFrom: 'admin-ui', skipUmls: String(skipUmls), autoApproveStep2: String(autoApproveStep2), forceNewRun: String(forceNewRun) });
        try {
            const response = await glossaryService.triggerHarvest('latest', options);
            if (response.status === 409) {
                trackEvent('DomainLibrary.HarvestAlreadyRunning', { status: '409' });
                // Fetch current status to populate lastHarvestStatus so the rich in-progress
                // banner (with progress bar) renders correctly in the 409 path.
                try {
                    const currentStatus = await glossaryService.getHarvestStatus();
                    setLastHarvestStatus(currentStatus);
                } catch (statusErr) {
                    trackException(statusErr, { feature: 'DomainLibrary', operation: '409StatusFetch' });
                }
                setHarvestNotice('A harvest is already running.');
                setHarvestNoticeType('info');
                startPolling(true); // already running — user wants fast feedback, use fast-poll mode
            } else if (response.status === 403) {
                setHarvesting(false);
                setHarvestNotice('You do not have permission to trigger a harvest.');
                setHarvestNoticeType('error');
                trackEvent('DomainLibrary.HarvestForbidden', { status: '403' });
            } else {
                // Immediate fetch for instant feedback — the pipeline may already
                // be running by the time we get here (Durable Functions starts fast).
                // Other callers (409, approveStep2) already fetch before startPolling.
                try {
                    const freshStatus = await glossaryService.getHarvestStatus();
                    setLastHarvestStatus(freshStatus);
                } catch (fetchErr) {
                    trackException(fetchErr, { feature: 'DomainLibrary', operation: 'immediateFetchAfterTrigger' });
                }
                setHarvestNotice('Harvest started. Polling for progress…');
                setHarvestNoticeType('info');
                startPolling(true); // manual trigger — fast-poll mode for first 5 polls
            }
        } catch (err) {
            setHarvesting(false);
            setHarvestNotice('Failed to trigger harvest. See console for details.');
            setHarvestNoticeType('error');
            trackException(err, { feature: 'DomainLibrary', operation: 'triggerHarvest' });
        }
    };

    const handlePauseHarvest = async () => {
        const instanceId = lastHarvestStatus && lastHarvestStatus.umlsVersion
            ? `harvest-${lastHarvestStatus.umlsVersion}`
            : null;
        if (!instanceId) return;
        try {
            await glossaryService.pauseHarvest(instanceId);
            const updated = await glossaryService.getHarvestStatus();
            setLastHarvestStatus(updated);
            trackEvent('DomainLibrary.HarvestPaused', { instanceId });
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'pauseHarvest' });
        }
    };

    const handleResumeHarvest = async () => {
        const instanceId = lastHarvestStatus && lastHarvestStatus.umlsVersion
            ? `harvest-${lastHarvestStatus.umlsVersion}`
            : null;
        if (!instanceId) return;
        try {
            await glossaryService.resumeHarvest(instanceId);
            const updated = await glossaryService.getHarvestStatus();
            setLastHarvestStatus(updated);
            setHarvesting(true);
            trackEvent('DomainLibrary.HarvestResumed', { instanceId });
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'resumeHarvest' });
        }
    };

    const handleStopHarvest = async () => {
        const instanceId = lastHarvestStatus && lastHarvestStatus.umlsVersion
            ? `harvest-${lastHarvestStatus.umlsVersion}`
            : null;
        if (!instanceId) return;
        setShowStopConfirm(false);
        try {
            await glossaryService.stopHarvest(instanceId);
            const updated = await glossaryService.getHarvestStatus();
            setLastHarvestStatus(updated);
            setHarvesting(false);
            stopPolling();
            trackEvent('DomainLibrary.HarvestStopped', { instanceId });
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'stopHarvest' });
            // If the orchestration is already terminal (409), unlock the button so the
            // user can re-trigger. Show the error so they know what happened.
            setHarvestNotice(`Stop failed: ${err.message}`);
            setHarvestNoticeType('error');
            setHarvesting(false);
            // Override local status to a non-active state so isHarvestActive → false
            if (lastHarvestStatus) {
                setLastHarvestStatus({ ...lastHarvestStatus, status: 'stopped' });
            }
            stopPolling();
        }
    };

    const handleApproveStep2 = async () => {
        const instanceId = lastHarvestStatus && lastHarvestStatus.umlsVersion
            ? `harvest-${lastHarvestStatus.umlsVersion}`
            : null;
        if (!instanceId || approvingStep2) return;
        setApprovingStep2(true);
        try {
            await glossaryService.approveStep2(instanceId);
            setStep2JustApproved(true);
            const updated = await glossaryService.getHarvestStatus();
            setLastHarvestStatus(updated);
            setHarvesting(true);
            setHarvestNotice('LLM enrichment submitted — polling for progress…');
            setHarvestNoticeType('info');
            startPolling(true);
            trackEvent('DomainLibrary.HarvestStep2Approved', { instanceId });
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'approveStep2' });
            // Surface the error to the user so they know to re-trigger rather than waiting
            setHarvestNotice(`Step 2 approval failed: ${err.message}. The orchestration may have timed out — try triggering a new harvest.`);
            setHarvestNoticeType('error');
        } finally {
            setApprovingStep2(false);
        }
    };

    const handleRetryStep2 = async () => {
        if (retryingStep2 || step2RetrySubmitted) return;
        setRetryingStep2(true);
        try {
            const options = {
                skipUmls: true,
                autoApproveStep2: true
            };
            if (forceNewRun) options.forceNewRun = true;
            const resp = await glossaryService.triggerHarvest('latest', options);
            if (resp.ok || resp.status === 202) {
                setStep2RetrySubmitted(true);
                setHarvesting(true);
                setHarvestNotice('Step 2 retry submitted \u2014 polling for progress\u2026');
                setHarvestNoticeType('info');
                startPolling(true);
                trackEvent('DomainLibrary.HarvestStep2Retry', { forceNewRun: String(forceNewRun) });
            } else if (resp.status === 409) {
                // Check if step1_complete — auto-approve Step 2 on existing orchestration
                const body = await resp.json().catch(() => ({}));
                if (body.currentStatus === 'step1_complete' && body.instanceId) {
                    try {
                        await glossaryService.approveStep2(body.instanceId);
                        setStep2RetrySubmitted(true);
                        setHarvesting(true);
                        setHarvestNotice('Step 2 approved on existing pipeline \u2014 polling for progress\u2026');
                        setHarvestNoticeType('info');
                        startPolling(true);
                        trackEvent('DomainLibrary.HarvestStep2AutoApproved', { instanceId: body.instanceId });
                    } catch (approveErr) {
                        setHarvestNotice(`Step 2 approval failed: ${approveErr.message}`);
                        setHarvestNoticeType('error');
                        trackException(approveErr, { feature: 'DomainLibrary', operation: 'retryStep2AutoApprove' });
                    }
                } else {
                    trackEvent('DomainLibrary.HarvestStep2RetryAlreadyRunning', { status: '409' });
                    setStep2RetrySubmitted(true);
                    setHarvestNotice('A harvest is already running. Joining in progress\u2026');
                    setHarvestNoticeType('info');
                    setHarvesting(true);
                    startPolling(true);
                }
            } else if (resp.status === 403) {
                setHarvestNotice('You do not have permission to retry the harvest.');
                setHarvestNoticeType('error');
                trackEvent('DomainLibrary.HarvestStep2RetryForbidden', { status: '403' });
            } else {
                const body = await resp.text().catch(() => '');
                setHarvestNotice(`Step 2 retry failed (${resp.status}): ${body || 'Unknown error'}`);
                setHarvestNoticeType('error');
                trackException(new Error(`Step 2 retry failed: ${resp.status}`), {
                    feature: 'DomainLibrary',
                    operation: 'retryStep2',
                    httpStatus: String(resp.status)
                });
            }
        } catch (err) {
            trackException(err, { feature: 'DomainLibrary', operation: 'retryStep2' });
            setHarvestNotice(`Step 2 retry error: ${err.message}`);
            setHarvestNoticeType('error');
        } finally {
            setRetryingStep2(false);
        }
    };

    const handleImportAll = async () => {
        if (!tenantId) return;
        const importable = specialties ? specialties.filter(s => s.termCount > 0) : [];
        if (importable.length === 0) return;

        setImportingAll(true);
        setImportAllProgress({ current: 0, total: importable.length });
        trackEvent('DomainLibrary.BulkImport.Started', { total: importable.length.toString(), tenantId });
        let successCount = 0;

        for (let i = 0; i < importable.length; i++) {
            const s = importable[i];
            setImportAllProgress({ current: i + 1, total: importable.length });
            try {
                await glossaryService.importPublicGlossary(s.medicalSpecialty, tenantId);
                trackEvent('DomainLibrary.BulkImport.SpecialtyImported', { medicalSpecialty: s.medicalSpecialty, tenantId });
                successCount++;
            } catch (err) {
                trackException(err, { feature: 'DomainLibrary', operation: 'importAll', medicalSpecialty: s.medicalSpecialty });
                // Non-fatal: continue importing remaining specialties
            }
        }

        setImportingAll(false);
        setImportAllProgress(null);
        trackEvent('DomainLibrary.BulkImport.Completed', {
            success: successCount.toString(),
            failed: (importable.length - successCount).toString(),
            total: importable.length.toString(),
            tenantId
        });
        setImportedToast(`${successCount} of ${importable.length} specialties imported to tenant glossaries.`);
        setTimeout(() => setImportedToast(''), 5000);
    };

    const handleImported = (specialtyName) => {
        setImportedToast(`"${specialtyName}" imported to tenant glossaries.`);
        setTimeout(() => setImportedToast(''), 4000);
    };

    if (loading) {
        return (
            <div className="dl-container">
                <div className="dl-loading" data-testid="domain-library-skeleton">
                    <Loader2 size={32} className="dl-spin" />
                    <p>Loading domain library…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dl-container">
                <div className="dl-error" data-testid="domain-library-error">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                    <button type="button" className="dl-retry-btn" onClick={loadLibrary}>Retry</button>
                </div>
            </div>
        );
    }

    // Derived: true when the pipeline is in any active state that must block re-triggering Step 1.
    // Uses the module-level ACTIVE_PIPELINE_STATUSES constant (same list as reconciliation check).
    const isHarvestActive = harvesting || (lastHarvestStatus && ACTIVE_PIPELINE_STATUSES.includes(lastHarvestStatus.status));

    return (
        <div className="dl-container" data-testid="domain-library-panel">
            <div className="dl-header">
                <div className="dl-header-row">
                    <div>
                        <h2 className="dl-title">Medical Domain Library</h2>
                        <p className="dl-subtitle">Pre-built UMLS medical specialty glossaries. Import any specialty into a tenant's glossary collection.</p>
                    </div>
                    {/* Import All Specialties — visible to all admins when a tenant is selected */}
                    {isAdmin && specialties && specialties.some(s => s.termCount > 0) && (
                        <button
                            type="button"
                            className={`dl-import-all-btn${importingAll ? ' dl-importing-all' : ''}`}
                            onClick={handleImportAll}
                            disabled={importingAll || !tenantId}
                            data-testid="domain-library-import-all-btn"
                            title={!tenantId ? 'Select a tenant above to import' : 'Import all harvested specialties to selected tenant'}
                        >
                            {importingAll
                                ? <><Loader2 size={16} className="dl-spin" /> Importing {importAllProgress?.current}/{importAllProgress?.total}…</>
                                : <><Download size={16} /> Import All Specialties</>
                            }
                        </button>
                    )}
                    {isSuperAdmin && (
                        <div className="dl-harvest-btn-group">
                            {/* isHarvestActive is derived before return() using ACTIVE_PIPELINE_STATUSES. */}
                            {/* It covers all pipeline states where re-triggering Step 1 would overwrite */}
                            {/* in-progress work (Bug #1 fix: was disabled={harvesting} only). */}
                            <button
                                type="button"
                                className={`dl-harvest-btn${isHarvestActive ? ' dl-harvesting' : ''}`}
                                onClick={handleTriggerHarvest}
                                disabled={isHarvestActive}
                                data-testid="domain-library-trigger-harvest-btn"
                                title="Step 1 of 2: Runs deterministic lookups (SNOMED, ICD-11, Wikidata). LLM gap-fill (Step 2) requires separate approval."
                            >
                                <RefreshCw size={16} className={isHarvestActive ? 'dl-spin' : ''} />
                                {isHarvestActive ? 'Pipeline Active\u2026' : 'Trigger Harvest (Step 1)'}
                            </button>
                            {/* Run Step 2 Only — visible when Step 1 data exists and pipeline is not active */}
                            {!isHarvestActive && specialties && specialties.some(s => s.termCount > 0) && (
                                <button
                                    type="button"
                                    className="dl-harvest-btn dl-step2-btn"
                                    onClick={handleRetryStep2}
                                    disabled={retryingStep2 || step2RetrySubmitted}
                                    data-testid="domain-library-run-step2-btn"
                                    title="Skip source lookups, run LLM enrichment (Step 2) directly on existing Step 1 data."
                                >
                                    {retryingStep2 ? <><Loader2 size={16} className="dl-spin" /> Submitting…</> : step2RetrySubmitted ? <><Loader2 size={16} className="dl-spin" /> Step 2 Starting…</> : 'Run Step 2 (LLM)'}
                                </button>
                            )}
                            <span className="dl-harvest-btn-note">{autoApproveStep2 ? 'LLM step auto-approved' : 'LLM step requires approval'}</span>
                            <button
                                type="button"
                                className="dl-advanced-toggle"
                                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                                data-testid="harvest-advanced-toggle"
                            >
                                {showAdvancedOptions ? 'Hide options' : 'Advanced'}
                            </button>
                            {showAdvancedOptions && (
                                <div className="dl-advanced-options" data-testid="harvest-advanced-options">
                                    <label className="dl-advanced-option">
                                        <input
                                            type="checkbox"
                                            checked={skipUmls}
                                            onChange={(e) => setSkipUmls(e.target.checked)}
                                            data-testid="harvest-opt-skip-umls"
                                        />
                                        <span>Skip UMLS seeding</span>
                                        <span className="dl-option-hint">Re-use existing blobs, re-run gap analysis</span>
                                    </label>
                                    <label className="dl-advanced-option">
                                        <input
                                            type="checkbox"
                                            checked={autoApproveStep2}
                                            onChange={(e) => setAutoApproveStep2(e.target.checked)}
                                            data-testid="harvest-opt-auto-approve"
                                        />
                                        <span>Auto-approve LLM step</span>
                                        <span className="dl-option-hint">Skip approval gate, run Step 2 immediately</span>
                                    </label>
                                    <label className="dl-advanced-option">
                                        <input
                                            type="checkbox"
                                            checked={forceNewRun}
                                            onChange={(e) => setForceNewRun(e.target.checked)}
                                            data-testid="harvest-opt-force-new"
                                        />
                                        <span>Force new run</span>
                                        <span className="dl-option-hint">Clear previous batch history and start fresh</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Lifecycle controls — shown when harvest is active (any ACTIVE_PIPELINE_STATUSES) */}
                    {isSuperAdmin && lastHarvestStatus && ACTIVE_PIPELINE_STATUSES.includes(lastHarvestStatus.status) && (
                        <div className="dl-lifecycle-controls" data-testid="harvest-lifecycle-controls">
                            {lastHarvestStatus.status === 'running' && (
                                <button
                                    type="button"
                                    className="dl-lifecycle-btn dl-pause-btn"
                                    onClick={handlePauseHarvest}
                                    data-testid="harvest-pause-btn"
                                    title="Pause the harvest pipeline"
                                >
                                    Pause
                                </button>
                            )}
                            {lastHarvestStatus.status === 'paused' && (
                                <button
                                    type="button"
                                    className="dl-lifecycle-btn dl-resume-btn"
                                    onClick={handleResumeHarvest}
                                    data-testid="harvest-resume-btn"
                                    title="Resume the paused harvest pipeline"
                                >
                                    Resume
                                </button>
                            )}
                            <button
                                type="button"
                                className="dl-lifecycle-btn dl-stop-btn"
                                onClick={() => setShowStopConfirm(true)}
                                data-testid="harvest-stop-btn"
                                title="Stop the harvest pipeline"
                            >
                                Stop
                            </button>
                        </div>
                    )}
                    <div className="dl-refresh-group">
                        <button
                            type="button"
                            className="dl-refresh-library-btn"
                            onClick={loadLibrary}
                            disabled={loading}
                            data-testid="domain-library-refresh-library-btn"
                            title="Refresh domain library"
                        >
                            <RefreshCw size={15} className={loading ? 'dl-spin' : ''} />
                            Refresh
                        </button>
                        {lastRefreshed && (
                            <span className="dl-last-refreshed">
                                {getRelativeTime(lastRefreshed.toISOString())}
                            </span>
                        )}
                    </div>
                </div>

                {/* Stop confirmation dialog */}
                {showStopConfirm && (
                    <div className="dl-stop-confirm-dialog" data-testid="harvest-stop-confirm-dialog">
                        <p className="dl-stop-confirm-message">Stop the harvest pipeline? This cannot be undone.</p>
                        <div className="dl-stop-confirm-actions">
                            <button
                                type="button"
                                className="dl-lifecycle-btn dl-stop-btn"
                                onClick={handleStopHarvest}
                                data-testid="harvest-stop-confirm-btn"
                            >
                                Stop Harvest
                            </button>
                            <button
                                type="button"
                                className="dl-lifecycle-btn"
                                onClick={() => setShowStopConfirm(false)}
                                data-testid="harvest-stop-cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Pipeline status panel — stepper, specialty progress, elapsed timer */}
                <HarvestPipelineStatus status={lastHarvestStatus} />

                {/* Paused banner */}
                {lastHarvestStatus && lastHarvestStatus.status === 'paused' && (
                    <div className="dl-harvest-notice dl-notice-warning" data-testid="harvest-paused-banner">
                        <AlertCircle size={16} />
                        <span>Harvest paused.</span>
                    </div>
                )}

                {/* Step 1 complete banner + stats panel */}
                {lastHarvestStatus && lastHarvestStatus.status === 'step1_complete' && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-step1-complete-banner">
                        <CheckCircle size={16} />
                        <div className="dl-harvest-banner-body">
                            <span className="dl-harvest-banner-title">Step 1 complete. Review statistics, then approve Step 2.</span>
                            {lastHarvestStatus.step1Stats && (
                                <div className="dl-step1-stats-panel" data-testid="harvest-step1-stats-panel">
                                    <p className="dl-step1-stats-summary">
                                        Total LLM gaps needed: <strong data-testid="harvest-step1-total-gaps">{lastHarvestStatus.step1Stats.totalLlmGapsNeeded}</strong>
                                    </p>
                                    {lastHarvestStatus.step1Stats.specialtyBreakdown && (
                                        <table className="dl-step1-stats-table">
                                            <thead>
                                                <tr>
                                                    <th>Specialty</th>
                                                    <th>LLM Gaps</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(lastHarvestStatus.step1Stats.specialtyBreakdown).map(([key, count]) => (
                                                    <tr key={key}>
                                                        <td>{key}</td>
                                                        <td>{count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                            <button
                                type="button"
                                className="dl-approve-step2-btn"
                                onClick={handleApproveStep2}
                                disabled={approvingStep2 || step2JustApproved || (lastHarvestStatus && lastHarvestStatus.step2Approved === true)}
                                data-testid="harvest-approve-step2-btn"
                            >
                                {approvingStep2 ? 'Submitting\u2026' : (step2JustApproved || (lastHarvestStatus && lastHarvestStatus.step2Approved === true)) ? 'LLM Step Approved \u2014 preparing\u2026' : 'Run LLM Step'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2 preparing banner — shown immediately after approval, before batch submission begins */}
                {lastHarvestStatus && lastHarvestStatus.status === 'step2_preparing' && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-step2-preparing-banner">
                        <Loader2 size={16} className="dl-spin" />
                        <span>Step 2 approved. Collecting and deduplicating LLM candidates&hellip;</span>
                    </div>
                )}

                {/* Step 2 failed banner — shown when LLM enrichment fails; Step 1 data is preserved */}
                {lastHarvestStatus && lastHarvestStatus.status === 'failed' && lastHarvestStatus.currentStep === 'step2' && (
                    <div className="dl-harvest-notice dl-notice-warning" data-testid="harvest-step2-failed-banner">
                        <AlertCircle size={16} />
                        <div className="dl-harvest-banner-body">
                            <span className="dl-harvest-banner-title">Step 2 (LLM enrichment) failed. Step 1 data is preserved.</span>
                            <p>You can retry the LLM step without re-running source lookups.</p>
                            <button
                                type="button"
                                className="dl-approve-step2-btn"
                                onClick={handleRetryStep2}
                                disabled={retryingStep2 || step2RetrySubmitted}
                                data-testid="harvest-retry-step2-btn"
                            >
                                {retryingStep2 ? 'Retrying\u2026' : step2RetrySubmitted ? 'Retry submitted \u2014 polling\u2026' : 'Retry LLM Step'}
                            </button>
                            <span className="dl-harvest-btn-note">Or use &ldquo;Trigger Harvest&rdquo; to re-run everything from scratch.</span>
                        </div>
                    </div>
                )}

                {/* Batch submitted / polling banners */}
                {lastHarvestStatus && (lastHarvestStatus.status === 'batch_submitted' || lastHarvestStatus.status === 'step2_batch_submitted') && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-batch-submitted-banner">
                        <Loader2 size={16} className="dl-spin" />
                        <span>Batch submitted to Gemini. Polling for results…</span>
                    </div>
                )}
                {lastHarvestStatus && lastHarvestStatus.status === 'batch_polling' && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-batch-polling-banner">
                        <Loader2 size={16} className="dl-spin" />
                        <span>
                            Polling Gemini Batch
                            {lastHarvestStatus.batchPagesTotal > 0 && (
                                <> ({lastHarvestStatus.batchPagesCompleted}/{lastHarvestStatus.batchPagesTotal} pages)</>
                            )}
                            {lastHarvestStatus.batchPollCount > 0 && (
                                <> &mdash; cycle {lastHarvestStatus.batchPollCount}</>
                            )}
                        </span>
                    </div>
                )}
                {lastHarvestStatus && lastHarvestStatus.status === 'step2_distributing' && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-distributing-banner">
                        <Loader2 size={16} className="dl-spin" />
                        <span>Distributing batch results to specialty blobs…</span>
                    </div>
                )}

                {/* Enhanced Batch Status Panel — shown during Step 2 LLM processing */}
                {lastHarvestStatus && ['batch_submitted', 'step2_batch_submitted', 'batch_polling', 'step2_distributing'].includes(lastHarvestStatus.status) && (
                    <div className="dl-batch-status-panel" data-testid="harvest-batch-status-panel">
                        <h4 className="dl-batch-status-title">LLM Batch Processing</h4>

                        {/* Progress bar for pages */}
                        {lastHarvestStatus.batchPagesTotal > 0 && (
                            <div className="dl-batch-progress">
                                <div
                                    className="dl-batch-progress-bar"
                                    data-testid="batch-progress-bar"
                                >
                                    <div
                                        className="dl-batch-progress-fill"
                                        style={{ width: `${Math.round((lastHarvestStatus.batchPagesCompleted / lastHarvestStatus.batchPagesTotal) * 100)}%` }}
                                    />
                                </div>
                                <span className="dl-batch-progress-text">
                                    {lastHarvestStatus.batchPagesCompleted}/{lastHarvestStatus.batchPagesTotal} pages completed
                                </span>
                            </div>
                        )}

                        {/* Key metrics grid */}
                        <div className="dl-batch-metrics">
                            {lastHarvestStatus.step1Stats && (
                                <div className="dl-batch-metric">
                                    <span className="dl-batch-metric-label">Terms for LLM</span>
                                    <span className="dl-batch-metric-value">
                                        {lastHarvestStatus.step1Stats.totalLlmGapsNeeded != null
                                            ? lastHarvestStatus.step1Stats.totalLlmGapsNeeded.toLocaleString()
                                            : '\u2014'}
                                    </span>
                                </div>
                            )}
                            {lastHarvestStatus.batchStatus && (
                                <div className="dl-batch-metric">
                                    <span className="dl-batch-metric-label">Batch Status</span>
                                    <span className={`dl-batch-metric-value dl-batch-status-${lastHarvestStatus.batchStatus}`}>
                                        {lastHarvestStatus.batchStatus}
                                    </span>
                                </div>
                            )}
                            <div className="dl-batch-metric">
                                <span className="dl-batch-metric-label">Poll Cycle</span>
                                <span className="dl-batch-metric-value">{lastHarvestStatus.batchPollCount || 0}</span>
                            </div>
                            {lastHarvestStatus.step2ApprovedAt && (
                                <div className="dl-batch-metric">
                                    <span className="dl-batch-metric-label">Step 2 Approved</span>
                                    <span className="dl-batch-metric-value">
                                        {formatDateTime(lastHarvestStatus.step2ApprovedAt)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Elapsed time */}
                        {lastHarvestStatus.batchSubmittedAt && (
                            <span className="dl-batch-elapsed">
                                Batch submitted: {formatDateTime(lastHarvestStatus.batchSubmittedAt)}
                                {getDuration(lastHarvestStatus.batchSubmittedAt, new Date().toISOString()) && (
                                    <> (elapsed: {getDuration(lastHarvestStatus.batchSubmittedAt, new Date().toISOString())})</>
                                )}
                            </span>
                        )}

                        {/* Trace ID */}
                        {lastHarvestStatus.traceId && (
                            <span className="dl-batch-trace">
                                TraceId: <code>{lastHarvestStatus.traceId}</code>
                            </span>
                        )}
                    </div>
                )}

                {/* Completed step 1 only banner — Step 2 never ran; offer one-click LLM step */}
                {lastHarvestStatus && lastHarvestStatus.status === 'completed_step1_only' && (
                    <div className="dl-harvest-notice dl-notice-info-blue" data-testid="harvest-completed-step1-only-banner">
                        <CheckCircle size={16} />
                        <div className="dl-harvest-banner-body">
                            <span className="dl-harvest-banner-title">Step 1 completed. Step 2 (LLM enrichment) was not run.</span>
                            <p>Run the LLM step without re-running source lookups.</p>
                            <button
                                type="button"
                                className="dl-approve-step2-btn"
                                onClick={handleRetryStep2}
                                disabled={retryingStep2 || step2RetrySubmitted}
                                data-testid="harvest-run-step2-btn"
                            >
                                {retryingStep2 ? 'Submitting\u2026' : step2RetrySubmitted ? 'Submitted \u2014 polling\u2026' : 'Run LLM Step'}
                            </button>
                        </div>
                    </div>
                )}

                {harvestNotice && (
                    <div className={`dl-harvest-notice dl-notice-${harvestNoticeType}`} data-testid="harvest-notice">
                        {harvestNoticeType === 'success' && <CheckCircle size={16} />}
                        {harvestNoticeType === 'error' && <AlertCircle size={16} />}
                        {harvestNoticeType === 'info' && <Loader2 size={16} className="dl-spin" />}
                        <div className="dl-harvest-banner-body">
                            {/* Completed banner */}
                            {harvestNoticeType === 'success' && lastHarvestStatus && (
                                <>
                                    <span className="dl-harvest-banner-title">
                                        Harvest completed — {formatDateTime(lastHarvestStatus.completedAt)}
                                        {getDuration(lastHarvestStatus.startedAt, lastHarvestStatus.completedAt) && (
                                            <> ({getDuration(lastHarvestStatus.startedAt, lastHarvestStatus.completedAt)})</>
                                        )}
                                    </span>
                                    {lastHarvestStatus.triggeredBy && (
                                        <span className="dl-harvest-banner-meta">
                                            Triggered by: {lastHarvestStatus.triggeredBy} ({formatTriggerType(lastHarvestStatus.triggeredByType)})
                                        </span>
                                    )}
                                    {lastHarvestStatus.traceId && (
                                        <span className="dl-harvest-banner-meta">
                                            TraceId: <code className="dl-harvest-trace" data-testid="harvest-trace-id">{lastHarvestStatus.traceId}</code>
                                        </span>
                                    )}
                                    {lastHarvestStatus.errorCount > 0 && (
                                        <span className="dl-harvest-error-warning">
                                            Warning: {lastHarvestStatus.errorCount} specialties had errors
                                        </span>
                                    )}
                                    {isSuperAdmin && (
                                        <CoverageSummary specialtyCoverage={lastHarvestStatus.specialtyCoverage} />
                                    )}
                                </>
                            )}
                            {/* Fallback for completed banner without lastHarvestStatus */}
                            {harvestNoticeType === 'success' && !lastHarvestStatus && (
                                <span className="dl-harvest-banner-title">{harvestNotice}</span>
                            )}
                            {/* In-progress banner */}
                            {harvestNoticeType === 'info' && lastHarvestStatus && lastHarvestStatus.status === 'running' && (
                                <>
                                    <span className="dl-harvest-banner-title">{harvestNotice}</span>
                                    <div
                                        className="dl-progress-bar-track"
                                        role="progressbar"
                                        aria-valuenow={lastHarvestStatus.specialtiesCompleted || 0}
                                        aria-valuemax={lastHarvestStatus.specialtiesTotal || 8}
                                        aria-valuemin={0}
                                        aria-label="Harvest progress"
                                    >
                                        <div
                                            className="dl-progress-bar-fill"
                                            style={{ width: `${Math.round(((lastHarvestStatus.specialtiesCompleted || 0) / (lastHarvestStatus.specialtiesTotal || 8)) * 100)}%` }}
                                        />
                                    </div>
                                    {(lastHarvestStatus.startedAt || lastHarvestStatus.triggeredBy) && (
                                        <span className="dl-harvest-banner-meta">
                                            {lastHarvestStatus.startedAt && <>Started {getRelativeTime(lastHarvestStatus.startedAt)}</>}
                                            {lastHarvestStatus.startedAt && lastHarvestStatus.triggeredBy && ' — '}
                                            {lastHarvestStatus.triggeredBy && <>Triggered by: {lastHarvestStatus.triggeredBy} ({formatTriggerType(lastHarvestStatus.triggeredByType)})</>}
                                        </span>
                                    )}
                                </>
                            )}
                            {/* In-progress banner without lastHarvestStatus (e.g. just after trigger click) */}
                            {harvestNoticeType === 'info' && (!lastHarvestStatus || lastHarvestStatus.status !== 'running') && (
                                <span className="dl-harvest-banner-title">{harvestNotice}</span>
                            )}
                            {/* Failed banner */}
                            {harvestNoticeType === 'error' && lastHarvestStatus && lastHarvestStatus.status === 'failed' && (
                                <>
                                    <span className="dl-harvest-banner-title">Harvest failed</span>
                                    {lastHarvestStatus.traceId && (
                                        <span className="dl-harvest-banner-meta">
                                            TraceId: <code className="dl-harvest-trace" data-testid="harvest-trace-id">{lastHarvestStatus.traceId}</code>
                                        </span>
                                    )}
                                </>
                            )}
                            {/* Fallback for error banner without lastHarvestStatus */}
                            {harvestNoticeType === 'error' && (!lastHarvestStatus || lastHarvestStatus.status !== 'failed') && (
                                <span className="dl-harvest-banner-title">{harvestNotice}</span>
                            )}
                        </div>
                    </div>
                )}

                {importedToast && (
                    <div className="dl-import-toast" data-testid="domain-library-import-toast">
                        <CheckCircle size={16} /> {importedToast}
                    </div>
                )}

                {!tenantId && (
                    <p className="dl-tenant-hint">
                        Select a tenant on the Tenant Glossaries tab to enable importing into that tenant.
                    </p>
                )}
            </div>

            {specialties && specialties.length > 0 && (
                <div className="dl-search-bar" data-testid="specialty-search">
                    <Search size={16} className="dl-search-icon" />
                    <input
                        type="text"
                        className="dl-search-input"
                        placeholder={`Search ${specialties.length} specialties…`}
                        value={specialtyFilter}
                        onChange={(e) => setSpecialtyFilter(e.target.value)}
                        data-testid="specialty-search-input"
                    />
                    {specialtyFilter && (
                        <button
                            type="button"
                            className="dl-search-clear"
                            onClick={() => setSpecialtyFilter('')}
                            data-testid="specialty-search-clear"
                            aria-label="Clear search"
                        >
                            &times;
                        </button>
                    )}
                </div>
            )}
            <div className="dl-specialty-list" data-testid="specialty-list">
                {specialties && specialties.length > 0 ? (() => {
                    const filtered = specialties
                        .filter(s => s.medicalSpecialty.toLowerCase().includes(specialtyFilter.toLowerCase()))
                        .sort((a, b) => a.medicalSpecialty.localeCompare(b.medicalSpecialty));
                    return filtered.length > 0 ? (
                        filtered.map((specialty) => (
                            <SpecialtyItem
                                key={specialty.medicalSpecialty}
                                specialty={specialty}
                                tenantId={tenantId}
                                isSuperAdmin={isSuperAdmin}
                                onImported={handleImported}
                                onError={setError}
                            />
                        ))
                    ) : (
                        <div className="dl-empty" data-testid="domain-library-no-match">
                            <p>No specialties match &ldquo;{specialtyFilter}&rdquo;</p>
                        </div>
                    );
                })() : (
                    <div className="dl-empty" data-testid="domain-library-empty">
                        <p>No specialties found in the domain library.</p>
                        {isSuperAdmin && (
                            <p>Click <strong>Trigger Harvest</strong> to run the harvest pipeline and populate it.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DomainLibrary;
